import { Mesh, MeshBuilder, TransformNode, Vector3 } from '@babylonjs/core';
import { AssetBasedConfig, DistributionConfig, SpawnPoint } from './AssetObject';
import { createEnemyBehavior } from './behavior/EnemyBehaviorFactory';
import { Enemy } from './Enemy';
import { GameConfig } from './GameConfig';
import { EntityGenerator, Level } from './Level';
import { EntitySoundConfig, NumberRange } from './LevelConfig';
import { MaterialService } from './MaterialService';
import { MeshExplode } from './MeshExplode';
import { AssetObjectConfig, MeshFactory } from './MeshFactory';
import { PlayerCollision } from './PlayerCollision';

/**
 * Behavior types for enemies
 */
export type EnemyBehaviorType =
  | 'idle'
  | 'patrol'
  | 'chase'
  | 'circle'
  | 'jump'
  | 'attack'
  | 'floatingBomber'
  | 'snipe' // Stationary sniper with aim laser
  | 'teleport' // Disappears and reappears near player
  | 'chargeJump' // Ballistic leap to player's position
  | 'mimic'; // Disguised as collectible until approached

/**
 * Configuration for enemy distribution along the path
 */
export interface EnemyDistributionConfig extends DistributionConfig {
  /**
   * Behavior type for this enemy
   */
  behavior: EnemyBehaviorType;

  /**
   * Lateral offset range from path center
   */
  offsetRange?: NumberRange;

  /**
   * Y offset from path
   */
  yOffset?: number;

  /**
   * Scale range for enemy
   */
  scale?: NumberRange;

  /**
   * Movement speed multiplier
   */
  speed?: number;

  /**
   * Detection range for behaviors (chase, attack)
   */
  detectionRange?: number;

  /**
   * Attack range for melee
   */
  attackRange?: number;

  /**
   * Patrol width (for patrol behavior)
   */
  patrolWidth?: number;

  /**
   * Circle radius (for circle behavior)
   */
  circleRadius?: number;

  /**
   * Jump height (for jump behavior)
   */
  jumpHeight?: number;

  /**
   * Float height above spawn point (for floatingBomber behavior)
   */
  floatHeight?: number;

  /**
   * Float radius - size of the floating motion circle (for floatingBomber behavior)
   */
  floatRadius?: number;

  /**
   * Float speed - slow/calm movement speed (for floatingBomber behavior)
   */
  floatSpeed?: number;

  /**
   * Bomb drop interval in seconds (for floatingBomber behavior)
   */
  bombInterval?: number;

  /**
   * Bomb mesh scale (for floatingBomber behavior)
   */
  bombScale?: number;

  /**
   * Snipe range – detection/fire range (for snipe behavior)
   */
  snipeRange?: number;

  /**
   * Snipe interval – aim duration before shooting (for snipe behavior)
   */
  snipeInterval?: number;

  /**
   * Teleport range – max distance from player to reappear (for teleport behavior)
   */
  teleportRange?: number;

  /**
   * Teleport interval – cooldown between teleports (for teleport behavior)
   */
  teleportInterval?: number;

  /**
   * Charge windup – wind-up duration before jump (for chargeJump behavior)
   */
  chargeWindup?: number;

  /**
   * Charge speed – jump arc speed (for chargeJump behavior)
   */
  chargeSpeed?: number;

  /**
   * Boulder speed – rolling speed (for rollingBoulder behavior)
   */
  boulderSpeed?: number;

  /**
   * Mimic type – disguise hint (for mimic behavior)
   */
  mimicType?: string;

  /**
   * Sound config for enemy
   */
  sound?: EntitySoundConfig;
}

/**
 * Configuration for enemy spawning in a level
 */
export interface EnemyConfig extends AssetBasedConfig<EnemyDistributionConfig> {}

/**
 * Generator for spawning enemies along the path at runtime
 */
export class EnemyGenerator implements EntityGenerator<EnemyConfig> {
  private spawnPoints: SpawnPoint<EnemyDistributionConfig>[] = [];
  private activeEntities: Map<string, Enemy> = new Map();
  private spawnRange: number;
  private despawnRange: number;

  constructor(public level: Level) {}

  /**
   * Generate spawn points based on enemy config
   * Called once during level build
   */
  async generate(config: EnemyConfig): Promise<void> {
    this.spawnRange = config.spawnRange;
    this.despawnRange = config.despawnRange;

    let spawnPointId = 0;

    const markovAssignments = new Map<number, EnemyDistributionConfig>();
    if (this.level.config.markov) {
      for (let i = 0; i < this.level.pathPoints.length; i++) {
        if (this.level.pathStates[i] === 'ENEMY') {
          const valid = config.distributions.filter((d) => {
            const minP = d.minPoint ?? 0;
            const maxP = d.maxPoint ?? this.level.pathPoints.length - 1;
            return i >= minP && i <= maxP;
          });
          if (valid.length > 0) {
            markovAssignments.set(i, this.level.randomArrayElement(valid));
          }
        }
      }
    }

    // Process each distribution config
    for (const distribution of config.distributions) {
      // Find matching asset objects
      const matchingAssets = config.assetObjects.filter((ao) => distribution.types.includes(ao.type));

      if (matchingAssets.length === 0) {
        console.warn(`[EnemyGenerator] No asset objects found for types: ${distribution.types}`);
        continue;
      }

      const interval = distribution.interval ?? 1;
      const minPoint = distribution.minPoint ?? 0;
      const maxPoint = distribution.maxPoint ?? this.level.pathPoints.length - 1;

      // Create spawn points along the path
      for (let i = 0; i < this.level.pathPoints.length; i += interval) {
        if (i < minPoint || i > maxPoint) {
          continue;
        }

        if (this.level.occupiedPathIndexes.includes(i)) {
          continue;
        }

        // if we only have a platform config, skip if we don't have a platform at this path index
        if (this.level.config.platform && !this.level.config.way) {
          // no instance at this index because it would be placed in the air
          const platform = this.level.getPlatformByPathIndex(i);
          if (!platform) {
            continue;
          }
          if (platform.config.moving || platform.config.crumbling) {
            // no instance on moving platforms, too stressful
            continue;
          }
        }

        if (this.level.config.markov) {
          if (this.level.pathStates[i] !== 'ENEMY' || markovAssignments.get(i) !== distribution) {
            continue;
          }
        } else {
          const probability = this.level.rng.uFloat32();
          const distributionProbability = distribution.probability + (distribution.progressionProbability ?? 0) * i;
          if (distributionProbability && probability > distributionProbability) {
            continue;
          }
          if (distribution.skipProbability && probability < distribution.skipProbability) {
            continue;
          }
        }

        const nextPoint = this.level.pathPoints[Math.min(i + 1, this.level.pathPoints.length - 1)];
        const prevPoint = this.level.pathPoints[Math.max(i - 1, 0)];

        // Calculate tangent and lateral vectors
        const tangent = nextPoint.subtract(prevPoint).normalize();
        const lateral = new Vector3(-tangent.z, 0, tangent.x);

        // Calculate spawn position with optional lateral offset
        let position = this.level.getMaxPathPosition(i);

        if (distribution.offsetRange) {
          const lateralOffset = this.level.floatInRange(distribution.offsetRange.min, distribution.offsetRange.max);
          position = position.add(lateral.scale(lateralOffset));
        }

        // Apply Y offset
        if (distribution.yOffset !== undefined) {
          position.y += distribution.yOffset;
        }

        // Select random asset from matching assets
        const assetObject = this.level.randomArrayElement(matchingAssets);

        this.spawnPoints.push({
          id: `enemy_${spawnPointId++}`,
          position,
          pathIndex: i,
          distribution,
          assetObject,
          spawned: false,
        });
        this.level.occupiedPathIndexes.push(i);
      }
    }
    this.level.enemies = this.spawnPoints.length;

    if (GameConfig.logDebug) {
      console.log(`[EnemyGenerator] Created ${this.spawnPoints.length} spawn points`);
    }
  }

  /**
   * Update - call every frame to manage spawning/despawning
   */
  update(deltaTime: number): void {
    if (!this.level.player) {
      return;
    }

    // Check spawn points for spawning
    for (const spawnPoint of this.spawnPoints) {
      if (spawnPoint.spawned) {
        continue;
      }

      const distance = this.level.getCameraDistance(spawnPoint.position);
      if (distance <= this.spawnRange) {
        this.spawnEnemy(spawnPoint);
      }
    }

    // Check active enemies for despawning and update
    for (const [id, enemy] of this.activeEntities) {
      const distance = this.level.getCameraDistance(enemy.spawnPoint);
      if (distance > this.despawnRange) {
        this.despawnEnemy(id);
      } else {
        enemy.update(deltaTime);
      }
    }
  }

  /**
   * Spawn an enemy at a spawn point
   */
  private spawnEnemy(spawnPoint: SpawnPoint<EnemyDistributionConfig>): void {
    const baseScale = spawnPoint.assetObject.scale || 1;
    let scale = baseScale;
    if (spawnPoint.distribution.scale) {
      const randomScale = this.level.floatInRange(spawnPoint.distribution.scale.min, spawnPoint.distribution.scale.max);
      scale = baseScale * randomScale;
    }

    spawnPoint.assetObject.deepClone = true;
    const config = AssetObjectConfig.builder()
      .object(spawnPoint.assetObject)
      .physicsShapeType(undefined)
      .scale(scale)
      // .onPlayerCollision((player, mesh) => {
      //   player.lives += 1;
      //   this.level.particles.createDisappearanceParticleSystem(mesh, 0.4);
      //   mesh.dispose();
      // })
      .build();
    const clonedMesh = config.buildMesh(this.level);
    MeshFactory.configureMesh(clonedMesh, config, this.level);

    // Apply position
    clonedMesh.position.copyFrom(spawnPoint.position);

    // Get all visible child meshes for particle effects later
    const childMeshes = clonedMesh.getChildMeshes(false, (n) => n instanceof Mesh) as Mesh[];

    // Compute world matrix to ensure positions are updated
    clonedMesh.computeWorldMatrix(true);
    for (const childMesh of childMeshes) {
      childMesh.computeWorldMatrix(true);
    }

    // Calculate LOCAL bounding box for the entire enemy (relative to clonedMesh)
    let minBounds = new Vector3(Infinity, Infinity, Infinity);
    let maxBounds = new Vector3(-Infinity, -Infinity, -Infinity);

    for (const childMesh of childMeshes) {
      //childMesh.refreshBoundingInfo();
      const boundingInfo = childMesh.getBoundingInfo();
      // Use local bounding box, not world
      const localMin = boundingInfo.boundingBox.minimum;
      const localMax = boundingInfo.boundingBox.maximum;
      // Transform to parent space (clonedMesh)
      const childPos = childMesh.position;

      minBounds = Vector3.Minimize(minBounds, localMin.add(childPos));
      maxBounds = Vector3.Maximize(maxBounds, localMax.add(childPos));
    }

    // Create invisible hitbox mesh that encompasses the entire enemy
    const size = maxBounds.subtract(minBounds);
    const center = minBounds.add(size.scale(0.5));

    // Use a capsule for better collision detection (height + radius)
    const hitboxHeight = Math.max(size.y, 0.5);
    // Reduce radius to be slimmer than the full bounding box to avoid "phantom" hits
    // Using factor of 4 to make it much tighter around the core body
    const hitboxRadius = Math.max(size.x, size.z) / 4;

    const hitbox = MeshBuilder.CreateCapsule(`${spawnPoint.id}_hitbox`, { height: hitboxHeight, radius: hitboxRadius }, this.level.scene);
    hitbox.parent = clonedMesh;
    // Position hitbox at the center of the local bounding box
    hitbox.position = center;
    // Use very low visibility - meshes with visibility=0 are ignored by intersection checks
    hitbox.visibility = 0.001;
    hitbox.isPickable = false;
    hitbox.checkCollisions = true;

    // Register collision on the single hitbox - handles the entire enemy
    hitbox.metadata = {
      enemy: true,
      projectileCollision: true,
      projectileDestoyable: true,
      playerCollision: new PlayerCollision(
        false, // disableCollision
        (level, player, event) => {
          const stomped = player.isStomping;
          if (player.dashing || player.isKicking || stomped || !player.vulnerable || level.game.config.godMode) {
            // Create particles for ALL visible child meshes
            for (const childMesh of childMeshes) {
              if (!childMesh.isDisposed()) {
                level.particles.createDisappearanceParticleSystem(childMesh, 0.4, MaterialService.colorRed);
                MeshExplode.explode(childMesh, this.level, 10, 5, -0.5, 20, 20);
              }
            }
            // Dispose the entire enemy (including hitbox)
            enemy.dispose();
            this.level.game.sound.play('enemydestroy');
            player.score += 100;
            player.enemies++;
            // Stomp bounce: give player a small upward impulse
            if (stomped) {
              player.stompBounce();
            }
          } else {
            player.collide(event, 'Killed by enemy!');
          }
        }
      ),
    };
    this.level.player?.registerCollisionMesh(hitbox);

    // Create behavior with cloned animations
    const behavior = createEnemyBehavior(spawnPoint.distribution, spawnPoint.position.clone());
    behavior.setAnimations(config.animations);

    // Create enemy
    const enemy = new Enemy(
      spawnPoint.id,
      clonedMesh,
      behavior,
      spawnPoint.distribution,
      spawnPoint.position.clone(),
      this.level,
      spawnPoint.pathIndex,
      clonedMesh as TransformNode,
      config.animations
    );

    hitbox.metadata.projectileDisposable = enemy;

    // Ensure mesh is visible
    clonedMesh.setEnabled(true);
    for (const child of clonedMesh.getChildMeshes()) {
      child.isVisible = true;
    }

    clonedMesh.receiveShadows = true;
    this.level.addShadowCaster(clonedMesh);

    this.activeEntities.set(spawnPoint.id, enemy);
    spawnPoint.spawned = true;
  }

  /**
   * Despawn an enemy
   */
  private despawnEnemy(id: string): void {
    const enemy = this.activeEntities.get(id);
    if (!enemy) {
      return;
    }

    enemy.dispose();
    this.activeEntities.delete(id);

    // Find and reset spawn point
    const spawnPoint = this.spawnPoints.find((sp) => sp.id === id);
    if (spawnPoint) {
      spawnPoint.spawned = false;
    }
  }

  /**
   * Dispose all enemies
   */
  dispose(): void {
    for (const [id, enemy] of this.activeEntities) {
      enemy.dispose();
    }
    this.activeEntities.clear();
    this.spawnPoints = [];
  }
}
